// pages/EditBlog.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Paper,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  Grid,
  IconButton,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  AddPhotoAlternate,
  Save,
  Computer,
  LocalHospital,
  Flight,
  Restaurant,
  Business,
  SportsEsports,
  School,
  MusicNote,
  Movie,
  FitnessCenter,
  Kitchen,
  Pets,
  AutoAwesome,
  Cancel,
} from '@mui/icons-material';
import { db } from '../../lib/firebase';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { uploadFileToGitHub, deleteFileByUrl } from '../../lib/githubUpload';
import AdvancedRichTextEditor from '../../components/AdvancedRichTextEditor';

const EditBlog = () => {
  const { id } = useParams();
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    category: 'Technology',
    tags: [],
  });
  const [tagInput, setTagInput] = useState('');
  const [coverImage, setCoverImage] = useState(null);
  const [coverImagePreview, setCoverImagePreview] = useState('');
  const [existingCoverImage, setExistingCoverImage] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [titleError, setTitleError] = useState('');
  const [contentError, setContentError] = useState('');
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // Categories with icons
  const categories = [
    { value: 'Technology', label: 'Technology', icon: <Computer />, color: '#2196F3' },
    { value: 'Health & Wellness', label: 'Health & Wellness', icon: <LocalHospital />, color: '#4CAF50' },
    { value: 'Travel', label: 'Travel', icon: <Flight />, color: '#FF9800' },
    { value: 'Food & Cooking', label: 'Food & Cooking', icon: <Restaurant />, color: '#F44336' },
    { value: 'Business', label: 'Business', icon: <Business />, color: '#9C27B0' },
    { value: 'Gaming', label: 'Gaming', icon: <SportsEsports />, color: '#673AB7' },
    { value: 'Education', label: 'Education', icon: <School />, color: '#3F51B5' },
    { value: 'Music', label: 'Music', icon: <MusicNote />, color: '#E91E63' },
    { value: 'Entertainment', label: 'Entertainment', icon: <Movie />, color: '#FF5722' },
    { value: 'Fitness', label: 'Fitness', icon: <FitnessCenter />, color: '#8BC34A' },
    { value: 'Lifestyle', label: 'Lifestyle', icon: <AutoAwesome />, color: '#FF4081' },
    { value: 'Home & Garden', label: 'Home & Garden', icon: <Kitchen />, color: '#795548' },
    { value: 'Pets & Animals', label: 'Pets & Animals', icon: <Pets />, color: '#FFA726' },
  ];

  useEffect(() => {
    fetchBlog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchBlog = async () => {
    try {
      const blogDoc = await getDoc(doc(db, 'blogs', id));
      if (blogDoc.exists()) {
        const data = blogDoc.data();
        if (currentUser?.uid !== data.userId) {
          navigate('/admin/blogs');
          return;
        }
        setFormData({
          title: data.title || '',
          content: data.content || '',
          excerpt: data.excerpt || '',
          category: data.category || 'Technology',
          tags: data.tags || [],
        });
        setExistingCoverImage(data.coverImage || '');
        if (data.coverImage) {
          setCoverImagePreview(data.coverImage);
        }
      } else {
        navigate('/admin/blogs');
      }
    } catch (err) {
      console.error('Error fetching blog:', err);
      setError('Failed to load blog');
    } finally {
      setFetching(false);
    }
  };

  const handleCoverImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size should be less than 5MB');
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please upload a valid image (JPEG, PNG, WEBP, or GIF)');
      return;
    }

    setCoverImage(file);
    const previewUrl = URL.createObjectURL(file);
    setCoverImagePreview(previewUrl);
    setError('');
  };

  const handleRemoveCoverImage = async () => {
    if (window.confirm('Are you sure you want to remove the cover image?')) {
      if (existingCoverImage && !coverImage) {
        await deleteFileByUrl(existingCoverImage);
        setExistingCoverImage('');
      }
      setCoverImagePreview('');
      setCoverImage(null);
      setExistingCoverImage('');
      setSuccess('Cover image removed');
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  const handleAddTag = () => {
    const trimmedTag = tagInput.trim();
    
    if (!trimmedTag) return;
    
    // Split tags by comma
    const newTags = trimmedTag.split(',').map(tag => tag.trim()).filter(tag => tag);
    
    // Filter out empty tags and duplicates
    const uniqueTags = [...new Set([...formData.tags, ...newTags])];
    
    // Remove any emojis or special characters (allow letters, numbers, spaces, hyphens)
    const cleanTags = uniqueTags.map(tag => 
      tag.replace(/[^\w\s-]/g, '').replace(/\s+/g, ' ').trim()
    ).filter(tag => tag.length > 0 && tag.length <= 30);
    
    if (cleanTags.length > 0) {
      setFormData({
        ...formData,
        tags: cleanTags,
      });
      setTagInput('');
      setError('');
    }
  };

  const handleTagInputKeyPress = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter(tag => tag !== tagToRemove),
    });
  };

  const validateForm = () => {
    let isValid = true;
    
    if (!formData.title.trim()) {
      setTitleError('Please enter a title');
      isValid = false;
    } else if (formData.title.length < 5) {
      setTitleError('Title must be at least 5 characters');
      isValid = false;
    } else if (formData.title.length > 200) {
      setTitleError('Title must be less than 200 characters');
      isValid = false;
    } else {
      setTitleError('');
    }
    
    if (!formData.content.trim()) {
      setContentError('Please enter content');
      isValid = false;
    } else if (formData.content.length < 50) {
      setContentError('Content must be at least 50 characters');
      isValid = false;
    } else {
      setContentError('');
    }
    
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      let coverImageUrl = existingCoverImage;
      
      // Upload new cover image if selected
      if (coverImage) {
        const uploadResult = await uploadFileToGitHub(coverImage);
        if (uploadResult.success) {
          // Delete old cover image if exists and different from new one
          if (existingCoverImage && existingCoverImage !== coverImageUrl) {
            await deleteFileByUrl(existingCoverImage);
          }
          coverImageUrl = uploadResult.url;
        } else {
          setError('Failed to upload cover image. Please try again.');
          setLoading(false);
          return;
        }
      }

      // Generate excerpt from content if not provided
      let excerpt = formData.excerpt.trim();
      if (!excerpt) {
        // Strip HTML tags and get plain text
        const plainText = formData.content.replace(/<[^>]*>/g, '');
        excerpt = plainText.substring(0, 200) + (plainText.length > 200 ? '...' : '');
      }

      const blogData = {
        title: formData.title.trim(),
        content: formData.content,
        excerpt: excerpt,
        category: formData.category,
        tags: formData.tags,
        coverImage: coverImageUrl,
        updatedAt: serverTimestamp(),
      };

      await updateDoc(doc(db, 'blogs', id), blogData);
      setSuccess('Blog post updated successfully!');
      setTimeout(() => {
        navigate('/admin/blogs');
      }, 1500);
    } catch (err) {
      console.error('Error updating blog:', err);
      setError('Failed to update blog. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold" sx={{ mb: 3 }}>
          Edit Blog Post
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          {/* Title Field */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Blog Title"
              value={formData.title}
              onChange={(e) => {
                setFormData({ ...formData, title: e.target.value });
                if (titleError) setTitleError('');
              }}
              required
              variant="outlined"
              placeholder="Enter an engaging title for your blog post..."
              error={!!titleError}
              helperText={titleError || `${formData.title.length}/200 characters`}
              inputProps={{ maxLength: 200 }}
            />
          </Box>

          {/* Category Selection with Icons */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="medium">
              Category
            </Typography>
            <Grid container spacing={1}>
              {categories.map((cat) => (
                <Grid item key={cat.value}>
                  <Button
                    variant={formData.category === cat.value ? 'contained' : 'outlined'}
                    onClick={() => setFormData({ ...formData, category: cat.value })}
                    startIcon={cat.icon}
                    sx={{
                      textTransform: 'none',
                      borderRadius: 3,
                      borderColor: formData.category === cat.value ? cat.color : undefined,
                      backgroundColor: formData.category === cat.value ? cat.color : undefined,
                      '&:hover': {
                        backgroundColor: formData.category === cat.value ? cat.color : undefined,
                        opacity: 0.9,
                      },
                    }}
                  >
                    {cat.label}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Excerpt Field */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Excerpt (Optional)"
              value={formData.excerpt}
              onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
              multiline
              rows={2}
              variant="outlined"
              placeholder="A short summary of your blog post (will be auto-generated from content if left empty)..."
              helperText={`${formData.excerpt.length}/300 characters`}
              inputProps={{ maxLength: 300 }}
            />
          </Box>

          {/* Cover Image Upload */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="medium">
              Cover Image
            </Typography>
            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
              <Button
                variant="outlined"
                component="label"
                startIcon={<AddPhotoAlternate />}
                sx={{ borderRadius: 2 }}
              >
                {existingCoverImage || coverImagePreview ? 'Change Cover Image' : 'Upload Cover Image'}
                <input
                  type="file"
                  hidden
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleCoverImageUpload}
                />
              </Button>
              {(coverImagePreview || existingCoverImage) && (
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    component="img"
                    src={coverImagePreview}
                    alt="Cover preview"
                    sx={{ height: 60, width: 100, objectFit: 'cover', borderRadius: 1 }}
                  />
                  <Tooltip title="Remove image">
                    <IconButton
                      size="small"
                      onClick={handleRemoveCoverImage}
                      color="error"
                    >
                      <Cancel />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>
            <Typography variant="caption" color="textSecondary">
              Supported formats: JPEG, PNG, WEBP, GIF (Max 5MB)
            </Typography>
          </Box>

          {/* Tags Input with Comma Separation */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="medium">
              Tags
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Add tags separated by commas (e.g., React, JavaScript, Web Development)..."
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyPress={handleTagInputKeyPress}
              onBlur={handleAddTag}
              helperText="Press Enter, comma, or click Add Tag to add multiple tags"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Button 
                      variant="text" 
                      onClick={handleAddTag}
                      size="small"
                    >
                      Add Tag
                    </Button>
                  </InputAdornment>
                ),
              }}
            />
            {formData.tags.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {formData.tags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      onDelete={() => handleRemoveTag(tag)}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ mb: 0.5 }}
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </Box>

          {/* Content Editor */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="medium">
              Content
            </Typography>
            <AdvancedRichTextEditor
              value={formData.content}
              onChange={(content) => {
                setFormData({ ...formData, content });
                if (contentError) setContentError('');
              }}
            />
            {contentError && (
              <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                {contentError}
              </Typography>
            )}
          </Box>

          {/* Action Buttons */}
          <Box display="flex" gap={2} justifyContent="flex-end" sx={{ mt: 3 }}>
            <Button 
              variant="outlined" 
              onClick={() => navigate('/admin/blogs')}
              sx={{ borderRadius: 2 }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} /> : <Save />}
              disabled={loading}
              sx={{ borderRadius: 2 }}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default EditBlog;